import Big from "big.js";
import { PAYMENT_STATUSES, TAX_MODES } from "../constants/index.js";
import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat.js";
dayjs.extend(localizedFormat);
/**
 * Calculates the tax amount for a given price, tax rate, and tax mode.
 * @param price - The price of the item.
 * @param taxRate - The tax rate percentage (e.g., 10 for 10%).
 * @param taxMode - The tax mode ('INCLUSIVE', 'EXCLUSIVE', 'NO_TAX').
 * @returns The calculated tax amount as a Big.js object.
 */
export const getTaxAmount = (price, taxRate, taxMode) => {
    if (!taxRate || taxMode === TAX_MODES.NO_TAX.id) {
        return new Big(0);
    }
    const rate = new Big(taxRate).div(100);
    if (taxMode === TAX_MODES.INCLUSIVE.id) {
        // For inclusive tax: tax = price - (price / (1 + rate))
        return price.minus(price.div(new Big(1).plus(rate))).round(2);
    }
    // For exclusive tax: tax = price * rate
    return price.times(rate).round(2);
};
/**
 * Calculates the price before tax for a given price, tax rate, and tax mode.
 * @param price - The price of the item.
 * @param taxRate - The tax rate percentage (e.g., 10 for 10%).
 * @param taxMode - The tax mode ('INCLUSIVE', 'EXCLUSIVE', 'NO_TAX').
 * @returns The price before tax as a Big.js object.
 */
export const getPriceBeforeTax = (price, taxRate, taxMode) => {
    if (!taxRate || taxMode !== TAX_MODES.INCLUSIVE.id) {
        // For exclusive or no tax, price before tax is the same as the given price.
        return price;
    }
    // For inclusive tax: priceBeforeTax = price / (1 + rate)
    const rate = new Big(taxRate).div(100);
    return price.div(new Big(1).plus(rate)).round(2);
};
export const getInvoiceSectionItemsTotal = (invoiceSectionItems) => {
    // item subtotal
    let subtotal = new Big(0);
    // total per tax rate
    const taxTotals = {};
    // total after tax
    let total = new Big(0);
    /**
     * Prices in this section were already calculated as base prices in the backend.
     */
    invoiceSectionItems.forEach((item) => {
        const { quantity, price, taxRate, taxMode, taxName } = item;
        const itemPrice = new Big(price ?? 0);
        const itemQuantity = new Big(quantity ?? 1);
        const lineTotal = itemPrice.times(itemQuantity);
        if (taxRate && taxMode !== TAX_MODES.NO_TAX.id) {
            const priceBeforeTax = getPriceBeforeTax(lineTotal, taxRate, taxMode);
            const taxAmount = getTaxAmount(lineTotal, taxRate, taxMode);
            subtotal = subtotal.plus(priceBeforeTax);
            const taxKey = taxName || "Tax";
            if (taxTotals[taxKey]) {
                taxTotals[taxKey].total = new Big(taxTotals[taxKey].total)
                    .plus(taxAmount)
                    .toNumber();
            }
            else {
                taxTotals[taxKey] = {
                    total: taxAmount.toNumber(),
                };
            }
        }
        else {
            // no tax is applied
            subtotal = subtotal.plus(lineTotal);
        }
    });
    // finalize total by adding the subtotal of items and all the totals per tax rate
    total = total.plus(subtotal);
    Object.values(taxTotals).forEach((taxTotal) => {
        total = total.plus(new Big(taxTotal.total));
    });
    return { subtotal: subtotal.toNumber(), taxTotals, total: total.toNumber() };
};
export const formatWithCurrency = (value, currency) => {
    const upperCode = currency.toUpperCase();
    value = new Big(value ?? 0);
    /**
     * Using Code for now, maybe consider symbol later in the future
     *
     * const formatter = new Intl.NumberFormat(systemDefaultValues.locale, {
     *   style: 'currency',
     *   currency: upperCode,
     *   minimumFractionDigits: 2,
     *   maximumFractionDigits: 2
     * });
     */
    const numericValue = value.toNumber();
    // return formatter.format(numericValue);
    return `${upperCode} ${numericValue.toFixed(2)}`;
};
/**
 * Takes raw invoice data and prepares it for rendering in the PDF document.
 * This contains all the logic previously in the component's useMemo hooks.
 */
export const prepareInvoicePdfData = (invoice, bankDetails, pdfHeaderImgUrl) => {
    let dueDate = invoice?.issueDate
        ? dayjs(invoice.issueDate).format("LL")
        : "-";
    if (invoice.paymentOption !== "ONLINE") {
        dueDate = invoice?.createdAt ? dayjs(invoice.createdAt).format("LL") : "-";
    }
    // --- Totals Logic ---
    const allInvoiceSectionItems = invoice.InvoiceSections?.flatMap((s) => s.InvoiceSectionItems ?? []) ?? [];
    const { subtotal, taxTotals } = getInvoiceSectionItemsTotal(allInvoiceSectionItems);
    // --- Payments Logic ---
    const paidPayments = invoice.Payments?.filter((p) => p.status === PAYMENT_STATUSES.PAID.id ||
        p.status === PAYMENT_STATUSES.REFUNDED.id) ?? [];
    const { grossAmount, feeAmount } = paidPayments.reduce((acc, p) => ({
        grossAmount: acc.grossAmount + p.grossAmount,
        feeAmount: acc.feeAmount + p.feeAmount,
    }), { grossAmount: 0, feeAmount: 0 });
    // --- Combine everything ---
    return {
        ...invoice,
        subtotal,
        taxTotals,
        grossAmount,
        feeAmount,
        issueDate: invoice?.issueDate ? dayjs(invoice.issueDate).format("LL") : "-",
        dueDate,
        invoiceNumber: invoice?.invoiceNumber ?? "-",
        reference: invoice?.reference ?? "-",
        bankAccountName: bankDetails?.bankAccountName ?? "",
        bankAccountNumber: bankDetails?.bankAccountNumber ?? "",
        bankAccountBSB: bankDetails?.bankAccountBSB ?? "",
        companyName: bankDetails?.companyName ?? "",
        companyAddress1: bankDetails?.companyAddress1 ?? "",
        companyAddress2: bankDetails?.companyAddress2 ?? "",
        companyLogo: pdfHeaderImgUrl ||
            "https://stsurveyatlasdev001.blob.core.windows.net/azure-storage-container-blob/logo-images/f161fe1b-7518-4905-8224-cbb798ab0029-tenantlogo",
    };
};
//# sourceMappingURL=invoice.js.map